import { formatDisplayDate } from '@/utils/date'

/**
 * Format an ISO 8601 date string for display (date only): "Jan 01, 2001".
 * @param {string|null|undefined} iso - ISO 8601 datetime string
 * @returns {string} Formatted date or empty string
 */
export function formatDate(iso) {
  return formatDisplayDate(iso)
}

/** Symbol fallback for public display so codes like PHP show as ₱ not "PHP". */
const CURRENCY_SYMBOLS = {
  USD: '$',
  EUR: '€',
  GBP: '£',
  PHP: '₱',
  AED: 'د.إ',
  CAD: 'C$',
  AUD: 'A$',
  JPY: '¥',
  CHF: 'Fr',
  NGN: '₦',
  MXN: 'MX$',
  BRL: 'R$',
}

/**
 * Format a number as currency. Use the restaurant's currency in restaurant context.
 * Uses a symbol (e.g. ₱ for PHP) when we have one, so public sites don't show raw codes like "PHP".
 * @param {number} amount - Numeric price
 * @param {string} [currency='USD'] - ISO 4217 currency code (e.g. from restaurant.currency)
 * @returns {string} Formatted string (e.g. "$10.00" or "₱10.00")
 */
export function formatCurrency(amount, currency = 'USD') {
  if (amount == null || Number.isNaN(Number(amount))) return '—'
  const code = (currency && String(currency).trim()) || 'USD'
  const num = Number(amount)
  const numberPart = new Intl.NumberFormat(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(num)
  const symbol = CURRENCY_SYMBOLS[code]
  if (symbol) {
    return symbol + numberPart
  }
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: code,
      minimumFractionDigits: 2,
    }).format(num)
  } catch {
    return code + ' ' + numberPart
  }
}

/**
 * Format a price for display, optionally hiding currency (number only).
 * @param {number} amount - Numeric price
 * @param {string} [currency='USD'] - ISO 4217 currency code
 * @param {boolean} [showCurrency=true] - When false, return only the number part (e.g. "10.00")
 * @returns {string}
 */
export function formatPriceDisplay(amount, currency = 'USD', showCurrency = true) {
  if (amount == null || Number.isNaN(Number(amount))) return '—'
  const num = Number(amount)
  const numberPart = new Intl.NumberFormat(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(num)
  if (!showCurrency) return numberPart
  return formatCurrency(num, currency)
}
